/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Navigation inside the enlarged view.
    this.addEventListener('click', this.#handleNavClick, opts);
    this.addEventListener('click', this.#suppressClickAfterDrag, { ...opts, capture: true });
    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);
    this.#dialogs.forEach((_, i) => this.#updateControls(i));
  }

  #dragStartX = null;
  #dragStartY = null;
  #suppressNextClick = false;

  get #dialogs() {
    return [...this.querySelectorAll('dialog')].filter((d) => d.querySelector('img[data-zoom-image]'));
  }

  #openIndex() {
    return this.#dialogs.findIndex((d) => d.open);
  }

  #updateControls(index) {
    const dialogs = this.#dialogs;
    const dialog = dialogs[index];
    if (!dialog) return;
    const prev = dialog.querySelector('[data-zoom-prev]');
    const next = dialog.querySelector('[data-zoom-next]');
    const counter = dialog.querySelector('[data-zoom-counter]');
    const single = dialogs.length < 2;
    [prev, next, counter].forEach((el) => el && (el.hidden = single));
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= dialogs.length - 1;
    if (counter) counter.textContent = `${index + 1} / ${dialogs.length}`;
  }

  #step(delta) {
    const dialogs = this.#dialogs;
    const current = this.#openIndex();
    if (current === -1) return;
    const target = current + delta;
    if (target < 0 || target >= dialogs.length) return;
    const nextDialog = dialogs[target];
    const img = nextDialog.querySelector('img[data-zoom-image]');
    if (img) img.loading = 'eager';
    this.#updateControls(target);
    nextDialog.showModal();
    dialogs[current].close();
    nextDialog.querySelector(delta < 0 ? '[data-zoom-prev]' : '[data-zoom-next]')?.focus({ preventScroll: true });
    this.#syncGallery(nextDialog);
  }

  // Keep the main slider on the image that was last viewed enlarged.
  #syncGallery(dialog) {
    const trigger = this.querySelector(`[commandfor="${dialog.id}"][data-zoom-preload]`);
    const slide = trigger?.closest('[data-gallery-slide]');
    const gallery = this.querySelector('s-product-gallery');
    if (!slide || !gallery || typeof gallery.goTo !== 'function') return;
    const index = [...gallery.querySelectorAll('[data-gallery-slide]')].indexOf(slide);
    if (index !== -1) gallery.goTo(index, 'auto');
  }

  #handleNavClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-zoom-prev]')) {
      event.preventDefault();
      this.#step(-1);
    } else if (event.target.closest('[data-zoom-next]')) {
      event.preventDefault();
      this.#step(1);
    }
  };

  #handleKeydown = (event) => {
    if (this.#openIndex() === -1) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #handlePointerDown = (event) => {
    if (this.#openIndex() === -1 || !event.isPrimary) return;
    if (event.target instanceof Element && event.target.closest('[data-zoom-prev],[data-zoom-next]')) return;
    this.#dragStartX = event.clientX;
    this.#dragStartY = event.clientY;
  };

  #handlePointerUp = (event) => {
    if (this.#dragStartX === null) return;
    const dx = event.clientX - this.#dragStartX;
    const dy = event.clientY - this.#dragStartY;
    this.#dragStartX = null;
    this.#dragStartY = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    // A drag is not a click: keep the image from closing the view.
    this.#suppressNextClick = true;
    setTimeout(() => (this.#suppressNextClick = false), 400);
    this.#step(dx < 0 ? 1 : -1);
  };

  #suppressClickAfterDrag = (event) => {
    if (!this.#suppressNextClick) return;
    this.#suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
